import React, { useState, useCallback, useEffect } from "react";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layers, GraduationCap, ClipboardList, Shuffle, Gamepad, MoreHorizontal, Pencil, Copy, Printer, GitMerge, Download, Trash2, Zap, Rocket, Building2, ScanSearch, Puzzle, Brain, Headphones, ArrowLeft, ListOrdered, Grid, Bookmark, ChevronDown, Share2, Crown, Network } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAppState } from "@/hooks/useAppState";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGamification } from "@/contexts/GamificationContext";
import { Deck, Card, StudyModes } from "@/types/linguadeck";

type StudyModeKey = "cards" | "learn" | "test" | "games" | "philology";

const DEFAULT_STUDY_MODES: StudyModes = { cards: true, learn: true, test: true, match: true, philology: true };
import { fetchDecksWithDueCards, fetchDueCardsCount, getSmartReviewSession, recordCardInteraction, recomputeAdaptiveMetricsForUser, fetchDecksByIds, buildFolderStudyCards } from "@/lib/db";
import SidebarDesktop from "@/components/SidebarDesktop";
import SidebarDrawer from "@/components/SidebarDrawer";
import BottomTabBar from "@/components/BottomTabBar";
import Topbar from "@/components/Topbar";
import HomeView from "@/components/HomeView";
import HomeDashboard from "@/components/HomeDashboard";
import FolderView from "@/components/FolderView";
import FlashcardView from "@/components/FlashcardView";
import LearnMode from "@/components/LearnMode";
import MatchMode from "@/components/MatchMode";
import TestStudyView from "@/components/TestStudyView";
import PhilologyMode from "@/components/PhilologyMode";
import CatMouseMode from "@/components/study/CatMouseMode";
import DefenderMode from "@/components/study/DefenderMode";
import TowerMode from "@/components/study/TowerMode";
import MemoryMixMode from "@/components/study/MemoryMixMode";
import CipherRebusMode from "@/components/study/CipherRebusMode";
import IqMode from "@/components/study/IqMode";
import SoundCloudsMode from "@/components/study/SoundCloudsMode";
import MemoryJumbleMode from "@/components/study/MemoryJumbleMode";
import BlitzRushMode from "@/components/study/BlitzRushMode";
import SwipeArenaMode from "@/components/study/SwipeArenaMode";
import SecretMosaicMode from "@/components/study/SecretMosaicMode";
import GameHeader from "@/components/study/GameHeader";
import DashboardView from "@/components/DashboardView";
import DeckModal from "@/components/DeckModal";
import CreateDeckPage from "@/pages/CreateDeckPage";
import EditDeckPage from "@/pages/EditDeckPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminSupportLogsView from "@/pages/AdminSupportLogsView";
import GlobalLibraryPage from "@/pages/GlobalLibraryPage";
import SettingsModal from "@/components/SettingsModal";
import { useGamificationStore } from "@/stores/useGamificationStore";
import { useTimeTrackingStore } from "@/stores/useTimeTrackingStore";
import { useDeckModeProgress } from "@/hooks/useDeckModeProgress";
import { useDeckFavorites } from "@/hooks/useDeckFavorites";
import { useThemeStore } from "@/stores/useThemeStore";
import ExplainModal from "@/components/ExplainModal";
import AchievementsDashboard from "@/components/AchievementsDashboard";
import QuickStudyModal from "@/components/QuickStudyModal";
import ExpressReviewWidget from "@/components/ExpressReviewWidget";
import NewFolderModal from "@/components/NewFolderModal";
import SaveToFolderModal from "@/components/SaveToFolderModal";
import ShareModal from "@/components/ShareModal";
import { addDecksToFolder } from "@/lib/db";
import Toast from "@/components/Toast";
import ExportModal from "@/components/ExportModal";
import DeleteDeckModal from "@/components/DeleteDeckModal";
import MergeModulesModal from "@/components/MergeModulesModal";
import { useModuleDuplicate, useModulePrint } from "@/components/ModuleActions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Index() {
  const { user, isTeacher } = useAuth();
  const params = useParams<{ deckId?: string; folderId?: string; gameMode?: string; mode?: string }>();
  const { deckId: urlDeckId, folderId: urlFolderId, gameMode: urlGameMode, mode: urlMode } = params;
  const location = useLocation();
  const navigate = useNavigate();
  const {
    state,
    updateState,
    addDeck,
    addDecks,
    deleteDeck,
    updateDeck,
    mergeCardInDeck,
    addFolder,
    refreshFolders,
    refreshDecks,
    ensureDeckWithCards,
    refetchDeck,
    addPoints,
    navTo,
    selectDeck,
  } = useAppState();
  const { t } = useTranslation();

  const GAME_MODES = [
    { id: "match", label: t("deckModal.studyModeMatch"), icon: Shuffle },
    { id: "cat-mouse", label: t("games.catMouse"), icon: Zap },
    { id: "defender", label: t("defender.title"), icon: Rocket },
    { id: "tower", label: t("tower.title"), icon: Building2 },
    { id: "memory-jumble", label: t("games.memoryJumble"), icon: ListOrdered },
    { id: "memory", label: t("games.memory"), icon: ScanSearch },
    { id: "rebus", label: t("games.rebus"), icon: Puzzle },
    { id: "iq", label: t("games.oddOneOut"), icon: Brain },
    { id: "audio", label: t("games.audioClouds"), icon: Headphones },
    { id: "blitz", label: t("games.blitz"), icon: Zap },
    { id: "swipe", label: t("games.swipe"), icon: Layers },
    { id: "mosaic", label: t("games.mosaic"), icon: Grid },
  ] as const;

  const COMING_SOON_MODES: { id: string; label: string; icon: typeof Grid; comingSoon?: true }[] = [
  ];

  const isTestRoute = location.pathname.startsWith("/test/") && !!urlDeckId;
  const isFolderRoute = location.pathname.startsWith("/folder/") && !!urlFolderId;
  const isFolderStudyRoute = location.pathname.includes("/study/") && !!urlFolderId && !!urlDeckId;
  const isLibraryStudyRoute = location.pathname.startsWith("/library/study/") && !!urlDeckId;
  const isHomeStudyRoute = location.pathname.startsWith("/study/") && !!urlDeckId;
  const isFlashcardStudyRoute = isLibraryStudyRoute || isFolderStudyRoute || isHomeStudyRoute;
  const isLibraryRoute = location.pathname === "/library";
  const isGlobalLibraryRoute = location.pathname === "/global-library";
  const isGlobalLibraryStudyRoute = location.pathname.startsWith("/global-library/study/");
  const isCreateDeckRoute = location.pathname === "/library/create";
  const isEditDeckRoute = location.pathname.startsWith("/library/edit/");
  const isHomeRoute = location.pathname === "/" && !isTestRoute && !isFolderRoute;
  const isFolderListRoute = isFolderRoute && !isFolderStudyRoute;
  const isGamificationRoute = location.pathname === "/gamification";
  const isMatchGamePath = location.pathname.startsWith("/study/match/") && !!urlDeckId;
  const isGameModeRoute =
    (!!urlGameMode && GAME_MODES.some((m) => m.id === urlGameMode) && !!urlDeckId) || isMatchGamePath;
  const isDashboardRoute = location.pathname === "/dashboard";
  const isAdminFeedbackRoute = location.pathname === "/admin/feedback";
  const isAdminSupportLogsRoute = location.pathname === "/admin/support-logs";
  const isAdminPageRoute = isAdminFeedbackRoute || isAdminSupportLogsRoute;
  const fromGlobalLibrary = (location.state as { fromGlobalLibrary?: boolean } | null)?.fromGlobalLibrary === true;
  /** When true, user came via "Перейти в модуль" — show deck dashboard first, do not auto-open study overlay (desktop) or fullscreen. */
  const showDashboardOnly = (location.state as { showDashboardOnly?: boolean } | null)?.showDashboardOnly === true;
  const studyBackPath =
    fromGlobalLibrary
      ? "/global-library"
      : isLibraryStudyRoute
        ? "/library"
        : isFolderStudyRoute && urlFolderId
          ? `/folder/${urlFolderId}`
          : "/";
  const effectiveView: typeof state.currentView =
    isAdminPageRoute
      ? "home"
      : isLibraryRoute || isLibraryStudyRoute || isCreateDeckRoute || isEditDeckRoute || isGlobalLibraryRoute || isGlobalLibraryStudyRoute
        ? "library"
        : isHomeRoute || isHomeStudyRoute
          ? "home"
          : isGamificationRoute
            ? "gamification"
            : isDashboardRoute
              ? "dashboard"
              : state.currentView;
  const {
    incrementStat,
    equippedTheme,
  } = useGamification();
  const setSystemTheme = useThemeStore((s) => s.setSystemTheme);
  const [searchParams] = useSearchParams();
  const gamificationTab = searchParams.get("tab") as "badges" | "streaks" | "titles" | "shop" | null;

  const [showSettings, setShowSettings] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [quickStudy, setQuickStudy] = useState<{ deckId: string; cardId: string } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [toast, setToast] = useState({ message: "", visible: false });
  const [studyFilter, setStudyFilter] = useState<"all" | "due">("all");
  const [activeStudyMode, setActiveStudyMode] = useState<StudyModeKey>("cards");
  const appliedInitialTabRef = React.useRef<string | null>(null);
    const [showExportModal, setShowExportModal] = useState(false);
  const [deleteModalDeck, setDeleteModalDeck] = useState<Deck | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showSaveToFolderModal, setShowSaveToFolderModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [isCardsFullscreen, setIsCardsFullscreen] = useState(false);
  const [swipeHistory, setSwipeHistory] = useState<number[]>([]);
  const [startedLearnMode, setStartedLearnMode] = useState(false);
  const [startedTestMode, setStartedTestMode] = useState(false);
  const isMobile = useIsMobile();

  const deckForProgress = state.decks.find((d) => d.id === urlDeckId);
  const { isDeckFavorite, toggleDeckFavorite } = useDeckFavorites();
  const { progress: modeProgress, gameProgress, updateProgress: updateModeProgress, updateGameProgress, refetchGameProgress, newlyMasteredGameIds, clearNewlyMastered, clearAllNewlyMastered } = useDeckModeProgress(
    isFlashcardStudyRoute ? urlDeckId ?? "" : "",
    deckForProgress?.cards?.length ?? 0
  );

  useEffect(() => {
    if (isFlashcardStudyRoute && urlDeckId) setSwipeHistory([]);
  }, [isFlashcardStudyRoute, urlDeckId]);

  const prevStudyModeRef = React.useRef<StudyModeKey>(activeStudyMode);

  const getDueCards = useCallback((deck: Deck): Card[] => {
    const cards = deck.cards ?? [];
    const now = new Date();
    return cards.filter((c) => !c.next_review || new Date(c.next_review) <= now);
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast({ message: msg, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2500);
  }, []);

  useEffect(() => {
    if (isTestRoute && urlDeckId) {
      updateState({ currentDeckId: urlDeckId, currentView: "test" });
    }
  }, [isTestRoute, urlDeckId, updateState]);

  useEffect(() => {
    if (isFlashcardStudyRoute && urlDeckId) {
      updateState({ currentDeckId: urlDeckId, currentView: "flashcards", cardIndex: 0, cardFlipped: false });
    }
  }, [isFlashcardStudyRoute, urlDeckId, updateState]);

  // Hard reset cardFlipped: (1) when switching to cards tab, (2) when returning from game, (3) when location changes to study route
  const prevCardsVisibleRef = React.useRef(false);
  const prevStudyPathRef = React.useRef<string>("");
  useEffect(() => {
    const showingCards =
      isFlashcardStudyRoute && activeStudyMode === "cards" && !isGameModeRoute;
    const wasShowingCards = prevCardsVisibleRef.current;
    prevCardsVisibleRef.current = showingCards;

    const studyPath = isFlashcardStudyRoute ? `${location.pathname}-${urlDeckId}` : "";
    const pathChangedToStudy = studyPath && prevStudyPathRef.current !== studyPath;
    prevStudyPathRef.current = studyPath;

    if (showingCards && (!wasShowingCards || pathChangedToStudy)) {
      updateState({ cardFlipped: false });
    }
  }, [isFlashcardStudyRoute, activeStudyMode, isGameModeRoute, location.pathname, urlDeckId, updateState]);

  // Track time spent in each mode during active study sessions
  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;

      if (isFlashcardStudyRoute && urlDeckId) {
        const { addModeSeconds } = useTimeTrackingStore.getState();
        if (isGameModeRoute) {
          addModeSeconds("games", 10);
        } else if (startedTestMode) {
          addModeSeconds("test", 10);
        } else if (startedLearnMode) {
          addModeSeconds("learn", 10);
        } else if (activeStudyMode === "cards") {
          addModeSeconds("cards", 10);
        }
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [isFlashcardStudyRoute, urlDeckId, isGameModeRoute, startedTestMode, startedLearnMode, activeStudyMode]);

  const handleNavigateToGameMode = useCallback(
    (deckId: string, gameMode: string, deck?: Deck) => {
      const prevState = (location.state as { fromGlobalLibrary?: boolean; deck?: Deck } | null) ?? {};
      navigate(`/study/${gameMode}/${deckId}`, { state: { ...prevState, ...(deck ? { deck } : {}) } });
    },
    [navigate, location.state]
  );

  const handleBackFromGameMode = useCallback(() => {
    const prevState = (location.state as { fromGlobalLibrary?: boolean; deck?: Deck } | null) ?? {};
    if (!urlDeckId) {
      navigate(prevState.fromGlobalLibrary ? "/global-library" : "/", { replace: true });
      return;
    }
    navigate(`/study/${urlDeckId}`, {
      state: { initialTab: "games" as const, ...(prevState.fromGlobalLibrary ? { fromGlobalLibrary: true } : {}), ...(prevState.deck ? { deck: prevState.deck } : {}) },
    });
  }, [navigate, urlDeckId, location.state]);

  useEffect(() => {
    if (isGamificationRoute) {
      updateState({ currentView: "gamification", cardFlipped: false });
    } else if (isDashboardRoute) {
      updateState({ currentView: "dashboard", cardFlipped: false });
    }
  }, [isGamificationRoute, isDashboardRoute, updateState]);

  // Sync state when on list routes (not study) — fixes blank screen when using browser Back.
  // Also reset cardFlipped when leaving study so it never persists across navigation.
  useEffect(() => {
    if (isLibraryRoute) {
      updateState({ currentView: "library", cardFlipped: false });
    } else if (isHomeRoute) {
      updateState({ currentView: "home", cardFlipped: false });
    } else if (isFolderListRoute) {
      updateState({ currentView: "library", cardFlipped: false });
    } else if (isCreateDeckRoute || isEditDeckRoute) {
      updateState({ cardFlipped: false });
    }
  }, [isLibraryRoute, isHomeRoute, isFolderListRoute, isCreateDeckRoute, isEditDeckRoute, updateState]);

  // Refetch game progress when returning from a game session so mini-bars update immediately
  const prevGameModeRouteRef = React.useRef(false);
  useEffect(() => {
    if (prevGameModeRouteRef.current && !isGameModeRoute && isFlashcardStudyRoute) {
      refetchGameProgress();
    }
    prevGameModeRouteRef.current = isGameModeRoute;
  }, [isGameModeRoute, isFlashcardStudyRoute, refetchGameProgress]);

  // Clear newly-mastered shine state after animation completes (fallback if onAnimationEnd doesn't fire)
  useEffect(() => {
    if (newlyMasteredGameIds.size === 0) return;
    const t = setTimeout(clearAllNewlyMastered, 1500);
    return () => clearTimeout(t);
  }, [newlyMasteredGameIds.size, clearAllNewlyMastered]);

  const handleSelectDeck = (id: string) => {
    setStudyFilter("all");
    selectDeck(id);
    setSidebarOpen(false);
  };

  const handleStudyDeck = useCallback(
    (deckId: string, mode?: StudyModeKey) => {
      setStudyFilter("all");
      setSidebarOpen(false);
      const path = location.pathname;
      // "Перейти в модуль": land on deck dashboard first (showDashboardOnly); do not auto-open study overlay
      const state = { ...(mode ? { initialTab: mode } : {}), showDashboardOnly: true };
      const folderMatch = path.match(/^\/folder\/([^/]+)/);
      if (folderMatch) {
        navigate(`/folder/${folderMatch[1]}/study/${deckId}`, { state });
      } else if (path === "/library" || path.startsWith("/library/study/")) {
        navigate(`/library/study/${deckId}`, { state });
      } else {
        navigate(`/study/${deckId}`, { state });
      }
    },
    [location.pathname, navigate]
  );

  /** Fast Track: navigate directly to study mode, bypassing dashboard. */
  const handleStudyDeckDirect = useCallback(
    (deckId: string, mode?: StudyModeKey) => {
      setStudyFilter("all");
      setSidebarOpen(false);
      const path = location.pathname;
      const initialTab = mode && ["cards", "learn", "test", "games", "philology"].includes(mode) ? mode : "cards";
      // Fullscreen overlay only applies to cards/learn/test on mobile. Philology and games render inline in the deck dashboard.
      const overlayMode = initialTab === "cards" || initialTab === "learn" || initialTab === "test";
      const fullscreenSuffix = isMobile && overlayMode
        ? `?mode=fullscreen${initialTab === "learn" ? "&type=quiz" : initialTab === "test" ? "&type=test" : ""}`
        : initialTab === "learn"
          ? "?type=quiz"
          : initialTab === "test"
            ? "?type=test"
            : "";
      const folderMatch = path.match(/^\/folder\/([^/]+)/);
      if (folderMatch) {
        navigate(`/folder/${folderMatch[1]}/study/${deckId}${fullscreenSuffix}`, { state: { initialTab } });
      } else if (path === "/library" || path.startsWith("/library/study/")) {
        navigate(`/library/study/${deckId}${fullscreenSuffix}`, { state: { initialTab } });
      } else {
        navigate(`/study/${deckId}${fullscreenSuffix}`, { state: { initialTab } });
      }
    },
    [location.pathname, navigate, isMobile]
  );

  /** Study entire folder as one session: load all decks with cards, merge cards, navigate to study with virtual deck. */
  const handleStudyFolder = useCallback(
    async (folderId?: string, deckIds?: string[], folderTitle?: string) => {
      if (!folderId || !deckIds?.length) return;
      try {
        const decksWithCards = await fetchDecksByIds(deckIds);
        const cards = buildFolderStudyCards(decksWithCards);
        if (cards.length === 0) {
          showToast(t("folder.noCardsToStudy"));
          return;
        }
        const firstDeck = decksWithCards[0];
        const virtualDeck: Deck = {
          id: `folder-${folderId}`,
          title: folderTitle?.trim() || t("folder.studyFolder"),
          lang: firstDeck?.lang ?? "en",
          emoji: "📁",
          cards,
        };
        const fullscreenSuffix = isMobile ? "?mode=fullscreen" : "";
        navigate(`/folder/${folderId}/study/folder-${folderId}${fullscreenSuffix}`, { state: { deck: virtualDeck } });
      } catch (err) {
        console.error("Failed to load folder for study:", err);
        showToast(t("folder.addMaterialsError"));
      }
    },
    [navigate, isMobile, t, showToast]
  );

  /** Review only overdue/weak cards from folder (uses getSmartReviewSession preset "repeat"). */
  const handleReviewFolder = useCallback(
    async (folderId: string, deckIds: string[], folderTitle?: string) => {
      if (!folderId || !deckIds?.length || !user?.id) return;
      try {
        const session = await getSmartReviewSession(user.id, {
          deckIds,
          preset: "repeat",
          maxCards: 500,
        });
        if (!session.cards.length) {
          showToast(t("folder.noCardsToReview"));
          return;
        }
        const virtualDeck: Deck = {
          id: `folder-review-${folderId}`,
          title: (folderTitle?.trim() || t("folder.studyFolder")) + " — " + t("folder.reviewDue"),
          lang: "en",
          emoji: "📁",
          cards: session.cards,
        };
        const fullscreenSuffix = isMobile ? "?mode=fullscreen" : "";
        navigate(`/folder/${folderId}/study/folder-review-${folderId}${fullscreenSuffix}`, { state: { deck: virtualDeck } });
      } catch (err) {
        console.error("Failed to load folder for review:", err);
        showToast(t("folder.addMaterialsError"));
      }
    },
    [navigate, isMobile, t, showToast, user?.id]
  );

  const handleDeleteDeck = (id: string) => {
    deleteDeck(id);
    showToast(t("home.deckDeleted"));
  };

  const handleRemoveTag = useCallback(
    async (tagName: string) => {
      const decksToUpdate = state.decks.filter((deck) => {
        if (deck.deckType?.trim() === tagName) return true;
        if (deck.level?.trim() === tagName) return true;
        const tags = (deck as Deck & { tags?: string[] }).tags;
        return Array.isArray(tags) && tags.some((t) => String(t).trim() === tagName);
      });
      for (const deck of decksToUpdate) {
        const updates: Partial<Deck> = {};
        if (deck.deckType?.trim() === tagName) updates.deckType = "";
        if (deck.level?.trim() === tagName) updates.level = "";
        const tags = (deck as Deck & { tags?: string[] }).tags;
        if (Array.isArray(tags) && tags.some((t) => String(t).trim() === tagName)) {
          (updates as Deck & { tags?: string[] }).tags = tags.filter((t) => String(t).trim() !== tagName);
        }
        if (Object.keys(updates).length > 0) {
          await updateDeck(deck.id, updates);
        }
      }
      if (decksToUpdate.length > 0) {
        showToast(t("home.tagDeleted"));
      }
    },
    [state.decks, updateDeck, showToast]
  );

  const handleCreateFolder = useCallback(
    async (title: string) => {
      const folder = await addFolder(title.trim());
      if (folder) {
        navigate(`/folder/${folder.id}`);
        showToast(t('toast.folderCreated', { title: folder.title }));
      }
    },
    [addFolder, navigate, showToast, t]
  );


  const handleResetCardFlip = useCallback(() => {
    updateState({ cardFlipped: false });
  }, [updateState]);

  const handleFlip = () => {
    const willShowBack = !state.cardFlipped;
    if (willShowBack) incrementStat("cardsViewed");
    updateState({ cardFlipped: !state.cardFlipped });
  };

  const handleMark = (correct: boolean) => {
    const deck = state.decks.find((d) => d.id === state.currentDeckId);
    if (!deck) return;

    const cards = studyFilter === "due" ? getDueCards(deck) : (deck.cards ?? []);
    if (!cards?.length) return;

    if (!correct) {
      showToast(t("toast.keepGoing"));
    }

    const isLastCard = state.cardIndex === cards.length - 1;
    if (isLastCard) {
      updateModeProgress("cards", 100);
      setSwipeHistory((h) => [...h, state.cardIndex]);
      refreshDecks();
      navigate(studyBackPath);
      return;
    }

    setSwipeHistory((h) => [...h, state.cardIndex]);
    const nextIndex = state.cardIndex + 1;
    updateState({ cardIndex: nextIndex, cardFlipped: false });
  };

  const handleUndo = () => {
    if (swipeHistory.length === 0) return;
    const prevIndex = swipeHistory[swipeHistory.length - 1];
    setSwipeHistory((h) => h.slice(0, -1));
    updateState({ cardIndex: prevIndex, cardFlipped: false });
  };

  const handleShuffle = () => {
    const deck = state.decks.find((d) => d.id === state.currentDeckId);
    if (!deck) return;
    const cardList = deck.cards ?? [];
    const shuffled = [...cardList];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    updateDeck(deck.id, { cards: shuffled });
    updateState({ cardIndex: 0, cardFlipped: false });
    showToast(t("toast.cardsShuffled"));
  };

  const handlePrevCard = () => {
    if (!studyDeck || state.cardIndex <= 0) return;
    updateState({ cardIndex: state.cardIndex - 1, cardFlipped: false });
  };

  const handleNextCard = () => {
    if (!studyDeck || state.cardIndex >= studyDeck.cards.length - 1) return;
    updateState({ cardIndex: state.cardIndex + 1, cardFlipped: false });
  };

  const testRouteDeck = isTestRoute ? state.decks.find((d) => d.id === urlDeckId) : null;
  const navStateDeck = (location.state as { deck?: Deck } | null)?.deck;
  const smartReviewDeck = urlDeckId === "smart-review" ? navStateDeck?.id === "smart-review" ? navStateDeck : null : null;
  // Prefer state.decks over navStateDeck so edit-card merges propagate (navStateDeck is stale snapshot from navigation)
  const studyRouteDeck =
    isFlashcardStudyRoute && urlDeckId
      ? smartReviewDeck ?? state.decks.find((d) => d.id === urlDeckId) ?? (navStateDeck?.id === urlDeckId ? navStateDeck : null)
      : null;
  /* currentDeck must be declared before any hook or callback that references it (e.g. useModuleDuplicate, useModulePrint, handleMergeComplete). */
  const currentDeck =
    isTestRoute && testRouteDeck
      ? testRouteDeck
      : isFlashcardStudyRoute && studyRouteDeck
        ? studyRouteDeck
        : state.decks.find((d) => d.id === state.currentDeckId);
  const studyDeck =
    currentDeck && studyFilter === "due"
      ? { ...currentDeck, cards: getDueCards(currentDeck) }
      : currentDeck;

  const { handleDuplicate: handleDuplicateDeck, isDuplicating } = useModuleDuplicate(currentDeck ?? null);
  const { handlePrint } = useModulePrint(currentDeck ?? null);

  // Load deck cards when on study route if deck has no cards (light-first loading). Skip smart-review and folder virtual deck.
  useEffect(() => {
    if (!isFlashcardStudyRoute || !urlDeckId || urlDeckId === "smart-review" || urlDeckId.startsWith("folder-")) return;
    const deck = state.decks.find((d) => d.id === urlDeckId);
    if (deck?.cards && deck.cards.length > 0) return;
    ensureDeckWithCards(urlDeckId);
  }, [isFlashcardStudyRoute, urlDeckId, state.decks, ensureDeckWithCards]);

  const handleBackFromLearnMode = useCallback(() => {
    setStartedLearnMode(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("type");
    const newSearch = url.searchParams.toString();
    window.history.replaceState({}, "", url.pathname + (newSearch ? `?${newSearch}` : ""));
  }, []);

  const handleMergeComplete = useCallback(
    (created: Deck) => {
      showToast(t("mergeModal.done"));
      setShowMergeModal(false);
      const fullscreenSuffix = isMobile ? "?mode=fullscreen" : "";
      navigate(`/library/study/${created.id}${fullscreenSuffix}`, { state: { deck: created } });
    },
    [showToast, t, navigate, isMobile]
  );

  // Study modes from deck. When opened from global library ("Перейти в модуль"), always show all modes (Learn, Test, Games). fromGlobalLibrary is declared above.
  const mergedModes = studyDeck?.studyModes
    ? { ...DEFAULT_STUDY_MODES, ...studyDeck.studyModes }
    : DEFAULT_STUDY_MODES;
  const studyModes: StudyModes = fromGlobalLibrary
    ? DEFAULT_STUDY_MODES
    : {
        cards: mergedModes.cards !== false,
        learn: mergedModes.learn !== false,
        test: mergedModes.test !== false,
        match: mergedModes.match !== false,
        philology: mergedModes.philology !== false,
      };

  const enabledModes: StudyModeKey[] = (
    ["cards", "learn", "test", "philology"] as const
  ).filter((k) => studyModes[k as keyof StudyModes]) as StudyModeKey[];
  const gamesEnabled = studyModes.match;
  const firstEnabledMode: StudyModeKey = enabledModes[0] ?? (gamesEnabled ? "games" : "cards");

  // Save cards progress when switching away from cards mode (must run after studyDeck is defined)
  useEffect(() => {
    if (studyDeck?.cards?.length && prevStudyModeRef.current === "cards" && activeStudyMode !== "cards") {
      const pct = Math.round(((state.cardIndex + 1) / studyDeck.cards.length) * 100);
      updateModeProgress("cards", Math.min(100, pct));
    }
    prevStudyModeRef.current = activeStudyMode;
  }, [activeStudyMode, studyDeck?.id, studyDeck?.cards?.length, state.cardIndex, updateModeProgress]);

  useEffect(() => {
    if (studyDeck && isFlashcardStudyRoute) {
      setActiveStudyMode((prev) => {
        const valid = enabledModes.includes(prev) || (prev === "games" && gamesEnabled);
        return valid ? prev : firstEnabledMode;
      });
    }
  }, [studyDeck?.id, firstEnabledMode, isFlashcardStudyRoute, studyModes.cards, studyModes.learn, studyModes.test, studyModes.match, studyModes.philology, gamesEnabled, enabledModes]);

  // Force fullscreen when ?mode=fullscreen (e.g. "Create and Study", "Start due review")
  useEffect(() => {
    if (isFlashcardStudyRoute && urlDeckId && isMobile && searchParams.get("mode") === "fullscreen") {
      setIsCardsFullscreen(true);
    }
  }, [isFlashcardStudyRoute, urlDeckId, isMobile, searchParams]);

  // Force Test mode start when ?type=test (persists across refresh)
  useEffect(() => {
    const typeTest = searchParams.get("type") === "test";
    if (!isFlashcardStudyRoute || !urlDeckId || !typeTest) return;
    if (!studyDeck?.cards?.length || activeStudyMode !== "test") return;
    setStartedTestMode(true);
  }, [isFlashcardStudyRoute, urlDeckId, searchParams, studyDeck?.cards?.length, activeStudyMode]);

  // History-based exit: push state when entering fullscreen player, handle device back button
  const playerHistoryPushedRef = React.useRef(false);
  const testHistoryPushedRef = React.useRef(false);
  useEffect(() => {
    if (!isFlashcardStudyRoute || !urlDeckId) {
      playerHistoryPushedRef.current = false;
      testHistoryPushedRef.current = false;
    }
  }, [isFlashcardStudyRoute, urlDeckId]);
  useEffect(() => {
    if (!isMobile || !isFlashcardStudyRoute || !urlDeckId) return;
    const onPopStateCards = () => {
      playerHistoryPushedRef.current = false;
      setIsCardsFullscreen(false);
    };
    const onPopStateTest = () => {
      testHistoryPushedRef.current = false;
      setStartedTestMode(false);
    };
    if (isCardsFullscreen && activeStudyMode === "cards" && studyDeck?.cards?.length) {
      if (!playerHistoryPushedRef.current) {
        playerHistoryPushedRef.current = true;
        window.history.pushState({ fullscreenPlayer: true }, "", window.location.href);
      }
      window.addEventListener("popstate", onPopStateCards);
      return () => window.removeEventListener("popstate", onPopStateCards);
    }
    if (startedTestMode && activeStudyMode === "test" && studyDeck?.cards?.length && studyModes.test) {
      if (!testHistoryPushedRef.current) {
        testHistoryPushedRef.current = true;
        window.history.pushState({ fullscreenTest: true }, "", window.location.href);
      }
      window.addEventListener("popstate", onPopStateTest);
      return () => window.removeEventListener("popstate", onPopStateTest);
    }
    playerHistoryPushedRef.current = false;
    testHistoryPushedRef.current = false;
  }, [isCardsFullscreen, startedTestMode, activeStudyMode, isFlashcardStudyRoute, urlDeckId, isMobile, studyDeck?.cards?.length, studyModes.test]);

  const handleBackFromPlayer = useCallback(() => {
    if (studyDeck?.cards?.length && activeStudyMode === "cards") {
      const pct = Math.round(((state.cardIndex + 1) / studyDeck.cards.length) * 100);
      updateModeProgress("cards", Math.min(100, pct));
    }
    if (playerHistoryPushedRef.current) {
      window.history.back();
    } else {
      setIsCardsFullscreen(false);
    }
  }, [studyDeck?.id, studyDeck?.cards?.length, activeStudyMode, state.cardIndex, updateModeProgress]);

  const handleBackFromTest = useCallback(() => {
    if (testHistoryPushedRef.current) {
      window.history.back();
    } else {
      setStartedTestMode(false);
      const url = new URL(window.location.href);
      url.searchParams.delete("type");
      url.searchParams.delete("mode");
      const newSearch = url.searchParams.toString();
      window.history.replaceState({}, "", url.pathname + (newSearch ? `?${newSearch}` : ""));
      if (!isMobile) {
        setActiveStudyMode((prev) => (prev === "test" ? "cards" : prev));
      }
    }
  }, [isMobile]);

  // Apply initialTab from navigation state, ?type=quiz, ?type=test, ?mode=, or smartReviewRecommendedMode when landing on deck view
  useEffect(() => {
    if (!isFlashcardStudyRoute) {
      appliedInitialTabRef.current = null;
      return;
    }
    if (!urlDeckId) return;
    const navState = location.state as { initialTab?: string; smartReviewRecommendedMode?: StudyModeKey } | null;
    const typeQuiz = searchParams.get("type") === "quiz";
    const typeTest = searchParams.get("type") === "test";
    const tab =
      urlDeckId === "smart-review"
        ? navState?.smartReviewRecommendedMode
        : navState?.initialTab ?? (typeQuiz ? "learn" : typeTest ? "test" : searchParams.get("mode"));
    const valid: StudyModeKey[] = ["cards", "learn", "test", "games", "philology"];
    if (!tab || !valid.includes(tab as StudyModeKey)) return;
    if (appliedInitialTabRef.current === urlDeckId) return;
    appliedInitialTabRef.current = urlDeckId;
    setActiveStudyMode(tab as StudyModeKey);
  }, [isFlashcardStudyRoute, urlDeckId, location.state, searchParams]);

  // Auto-start Learn (Quiz) mode when ?type=quiz or ?autostart=learn or Smart Review recommended learn
  useEffect(() => {
    const typeQuiz = searchParams.get("type") === "quiz";
    const autostartLearn = searchParams.get("autostart") === "learn";
    const navState = location.state as { smartReviewRecommendedMode?: StudyModeKey } | null;
    const smartReviewLearn = urlDeckId === "smart-review" && navState?.smartReviewRecommendedMode === "learn";
    if (!isFlashcardStudyRoute || !urlDeckId || (!typeQuiz && !autostartLearn && !smartReviewLearn)) return;
    if (!studyDeck?.cards?.length || activeStudyMode !== "learn") return;
    setStartedLearnMode(true);
    // Clear only autostart from URL; keep type=quiz so refresh preserves Quiz mode
    if (autostartLearn) {
      const url = new URL(window.location.href);
      url.searchParams.delete("autostart");
      const newSearch = url.searchParams.toString();
      const newPath = url.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState({}, "", newPath);
    }
  }, [isFlashcardStudyRoute, urlDeckId, searchParams, studyDeck?.cards?.length, activeStudyMode]);

  // Show Test UI when: on /test/:deckId OR flashcard study route with activeMode === 'test'
  const showTestView =
    (isTestRoute && !!testRouteDeck) ||
    (isFlashcardStudyRoute &&
      !!studyDeck &&
      activeStudyMode === "test" &&
      studyModes.test);

  // Tell time tracker when user is actively studying (flashcards or test)
  const isInStudyView = (effectiveView === "flashcards" && !!studyDeck?.cards?.length) || showTestView;
  useEffect(() => {
    useTimeTrackingStore.getState().setInStudyView(isInStudyView);
  }, [isInStudyView]);
  const handleStartDueReview = useCallback(async () => {
    if (!user?.id) return;
    try {
      const withDue = await fetchDecksWithDueCards(user.id);
      if (withDue.length === 0) {
        showToast(t("dashboard.noCardsToReview"));
        return;
      }
      setStudyFilter("due");
      const fullscreenSuffix = isMobile ? "?mode=fullscreen" : "";
      navigate(`/study/${withDue[0].deckId}${fullscreenSuffix}`);
    } catch (err) {
      console.warn("[SRS] fetchDecksWithDueCards:", err);
      showToast(t("toast.failedToLoadCards"));
    }
  }, [user?.id, navigate, showToast, isMobile]);

  const handleStartSmartReview = useCallback(async () => {
    if (!user?.id) return;
    try {
      recomputeAdaptiveMetricsForUser(user.id, { limit: 100 }).catch(() => {});
      const session = await getSmartReviewSession(user.id, { preset: "today", maxCards: 50 });
      if (!session.cards.length) {
        showToast(t("smartReview.noCardsReady"));
        return;
      }
      const deckIdByCardId = session.deckIdByCardId;
      const cardsWithDeckId = session.cards.map((c) => ({
        ...c,
        deckId: deckIdByCardId[c.id!] ?? c.id,
      })) as (Card & { deckId?: string })[];
      const recommendedMode = session.summary.recommendedPrimaryMode;
      const virtualDeck: Deck = {
        id: "smart-review",
        title: t("smartReview.title"),
        lang: "en",
        emoji: "🧠",
        cards: cardsWithDeckId,
        studyModes: {
          cards: true,
          learn: recommendedMode === "learn",
          test: recommendedMode === "test",
          match: false,
        },
      };
      const fullscreenSuffix = isMobile ? "?mode=fullscreen" : "";
      navigate(`/study/smart-review${fullscreenSuffix}`, {
        state: { deck: virtualDeck, smartReviewRecommendedMode: recommendedMode },
      });
    } catch (err) {
      console.warn("[SmartReview] getSmartReviewSession failed:", err);
      showToast(t("toast.failedToLoadCards"));
    }
  }, [user?.id, navigate, showToast, t, isMobile]);

  const isMatrixTheme = equippedTheme === "matrixTheme";
  const isNeonTheme = equippedTheme === "neonTheme";
  const isSakuraTheme = equippedTheme === "sakuraTheme";
  const isCyberpunkTheme = equippedTheme === "cyberpunkTheme";

  /** Fullscreen study overlays: cards (mobile) or Quiz/Learn (mobile + desktop) or Test (mobile + desktop). When active, hide Topbar + BottomTabBar. When showDashboardOnly, show overlay only after user clicks Start (startedLearnMode/startedTestMode/isCardsFullscreen). */
  const isStudyFullscreenOverlay =
    isFlashcardStudyRoute &&
    studyDeck?.cards &&
    studyDeck.cards.length > 0 &&
    (showDashboardOnly
      ? ((activeStudyMode === "cards" && isCardsFullscreen) ||
          (activeStudyMode === "learn" && startedLearnMode) ||
          (activeStudyMode === "test" && startedTestMode && studyModes.test))
      : ((isMobile && activeStudyMode === "cards" && isCardsFullscreen) ||
          (activeStudyMode === "learn" && startedLearnMode) ||
          (activeStudyMode === "test" && startedTestMode && studyModes.test)));

  return (
    <>
    <div
      className="main-app-layout flex min-h-[100dvh] w-full overflow-hidden bg-background m-0 p-0 md:h-[100dvh] md:max-h-[100dvh]"
      style={
        isMatrixTheme || isNeonTheme || isSakuraTheme || isCyberpunkTheme
          ? { background: "transparent", position: "relative", zIndex: 10 }
          : { position: "relative", zIndex: 10 }
      }
    >
      {/* Mobile: Slide-out drawer (hamburger menu) */}
      <SidebarDrawer
        open={sidebarOpen}
        onOpenChange={setSidebarOpen}
        folders={state.folders ?? []}
        currentView={effectiveView}
        onNavTo={(view) => {
          navTo(view);
          if (view === "home") navigate("/");
          if (view === "library") navigate("/library");
          if (view === "dashboard") navigate("/dashboard");
          if (view === "gamification") navigate("/gamification");
        }}
        onNewDeck={(type) => navigate(`/library/create?type=${type}`)}
        onCreateFolder={() => setShowFolderModal(true)}
      />

      {/* Fullscreen Test overlay — same as Cards/Learn: shown only after user clicks "Начать тест" (Start test). */}
      {activeStudyMode === "test" &&
        startedTestMode &&
        studyModes.test &&
        studyDeck?.cards &&
        studyDeck.cards.length > 0 &&
        isFlashcardStudyRoute && (
          <div
            className="fixed inset-0 z-40 flex flex-col bg-background"
            data-study-test-fullscreen
          >
            <TestStudyView
              deck={studyDeck}
              onBack={handleBackFromTest}
              onNext={undefined}
              onMark={undefined}
              onTestComplete={(r) => updateModeProgress("test", r.total > 0 ? Math.round((r.correctCount / r.total) * 100) : 0)}
              fullscreenMobile
            />
          </div>
        )}

      {/* Mobile: Fullscreen cards study overlay — hides header/tabs, gesture-driven UX */}
      {isMobile &&
        activeStudyMode === "cards" &&
        isCardsFullscreen &&
        studyDeck?.cards &&
        studyDeck.cards.length > 0 &&
        isFlashcardStudyRoute && (
          <div
            className="fixed inset-0 z-40 flex flex-col bg-background"
            data-study-cards-fullscreen
          >
            <FlashcardView
              key={`flashcard-mobile-${urlDeckId}-${state.cardIndex}-${(studyDeck.cards[state.cardIndex]?.id ?? state.cardIndex)}-${location.key}`}
              deck={studyDeck}
              cardIndex={state.cardIndex}
              cardFlipped={state.cardFlipped}
              isTeacher={isTeacher}
              onMountReset={handleResetCardFlip}
              onFlip={handleFlip}
              onMark={handleMark}
              onShuffle={handleShuffle}
              onShowExplain={() => setShowExplain(true)}
              onPrevCard={handlePrevCard}
              onNextCard={handleNextCard}
              onBack={handleBackFromPlayer}
              onMarkedKnow={(nextReviewDays) => {
                if (nextReviewDays >= 1) {
                  showToast(t("flashcard.nextReviewIn", { days: nextReviewDays }));
                }
              }}
              onUpdateCard={(cardIndex, updates) => {
                if (!studyDeck) return;
                const card = studyDeck.cards?.[cardIndex];
                const fullDeck = state.decks.find((d) => d.id === studyDeck.id);
                const fullIndex =
                  card?.id && fullDeck ? fullDeck.cards?.findIndex((c) => c.id === card.id) ?? -1 : -1;
                const idx = fullIndex >= 0 ? fullIndex : cardIndex;
                mergeCardInDeck(studyDeck.id, idx, updates);
                refetchDeck(studyDeck.id);
                if (typeof console !== "undefined" && console.info) {
                  console.info("[Study] study_card_state_synced_after_edit", {
                    cardId: card?.id,
                    hasImageUrl: updates.imageUrl !== undefined,
                    keys: Object.keys(updates),
                  });
                }
              }}
              onCardSrsUpdated={(cardIndex, cardId, srsData) => {
                if (!studyDeck) return;
                const deck = state.decks.find((d) => d.id === studyDeck.id);
                const fullIndex = deck?.cards?.findIndex((c) => c.id === cardId) ?? -1;
                const idx = fullIndex >= 0 ? fullIndex : cardIndex;
                mergeCardInDeck(studyDeck.id, idx, srsData);
              }}
              fullscreenMobile
              onUndo={handleUndo}
              canUndo={swipeHistory.length > 0}
              onOpenSettings={() => setShowSettings(true)}
            />
          </div>
        )}

      {/* Print-only: deck title + all cards (visible only in @media print) */}
      {currentDeck && (
        <div className="print-deck-content" aria-hidden>
          <h1 className="print-deck-title">{currentDeck.emoji} {currentDeck.title}</h1>
          <div className="print-cards-list">
            {(currentDeck.cards ?? []).map((card, i) => (
              <div key={card.id ?? i} className="print-card-item">
                <div className="print-card-front"><strong>{t("flashcard.question", "Question")}:</strong> {card.front}</div>
                <div className="print-card-back"><strong>{t("flashcard.answer", "Answer")}:</strong> {card.back}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Desktop: Sidebar (hidden on mobile; mobile uses BottomTabBar + SidebarDrawer) */}
      <SidebarDesktop
        folders={state.folders ?? []}
        currentView={effectiveView}
        onNavTo={(view) => {
          navTo(view);
          if (view === "home") navigate("/");
          if (view === "library") navigate("/library");
          if (view === "dashboard") navigate("/dashboard");
          if (view === "gamification") navigate("/gamification");
          if ((isTestRoute || isFolderRoute) && (view === "home" || view === "dashboard")) {
            navigate("/");
          }
        }}
        onNewDeck={(type) => navigate(`/library/create?type=${type}`)}
        onCreateFolder={() => setShowFolderModal(true)}
      />

      {/* Main Content Column — w-full on mobile when sidebar collapsed */}
      <div className="no-print flex flex-1 flex-col min-h-[100dvh] min-w-0 w-full md:w-auto m-0 p-0 relative md:h-[100dvh] md:min-h-0">
        {/* Header Container — hidden when fullscreen study (cards or Quiz) overlay is active */}
        {!isStudyFullscreenOverlay && (
        <header className="sticky top-0 w-full border-b border-border bg-card m-0 p-0 shrink-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-card/95">
          <Topbar
          currentView={effectiveView}
          currentDeckTitle={currentDeck?.title}
          isGlobalLibrary={isGlobalLibraryRoute || isGlobalLibraryStudyRoute}
          showDeckDashboardHeader={isFlashcardStudyRoute && !!studyDeck && !isGameModeRoute && (enabledModes.length > 0 || gamesEnabled)}
          deckDashboardMode={isFlashcardStudyRoute && studyDeck ? activeStudyMode : undefined}
          isTeacher={isTeacher}
          points={state.points}
          onOpenSettings={() => setShowSettings(true)}
          onOpenDraw={() => setSidebarOpen(true)}
          onNewDeck={(type) => navigate(`/library/create?type=${type}`)}
          onCreateFolder={() => setShowFolderModal(true)}
          onNavTo={(view) => {
            navTo(view);
            if (view === "home") navigate("/");
            if (view === "library") navigate("/library");
            if (view === "gamification") navigate("/gamification");
            if (view === "dashboard") navigate("/dashboard");
            if ((isTestRoute || isFolderRoute) && (view === "home" || view === "dashboard")) {
              navigate("/");
            }
          }}
          onOpenAchievements={() => navigate("/gamification")}
        />
        </header>
        )}

        {/* Scrollable Page Content — extra bottom padding on mobile for tab bar + safe area; tighter top padding for create */}
        <main
          className={`flex-1 overflow-y-auto overflow-x-hidden m-0 relative z-0 min-w-0 max-w-full min-h-full bg-background pb-[calc(5rem+env(safe-area-inset-bottom))] md:min-h-0 md:pb-6 ${isCreateDeckRoute ? "px-2 pt-2 sm:px-4 sm:pt-3 md:p-6" : "p-6"}`}
        >
          {isAdminFeedbackRoute && (
            <AdminFeedbackPage />
          )}
          {isAdminSupportLogsRoute && (
            <AdminSupportLogsView />
          )}
          {isCreateDeckRoute && (
            <CreateDeckPage
              addDeck={addDeck}
              addDecks={addDecks}
              state={{ aiModel: state.aiModel, pollinationsKey: state.pollinationsKey, geminiKey: state.geminiKey }}
              onDeckHydratedForStudy={(deck) => {
                updateState((prev) => ({
                  ...prev,
                  decks: prev.decks.map((d) => (d.id === deck.id ? deck : d)),
                }));
              }}
            />
          )}
          {isEditDeckRoute && (
            <EditDeckPage updateDeck={updateDeck} />
          )}
          {!isCreateDeckRoute && isFolderListRoute && urlFolderId && (
            <FolderView
              folderId={urlFolderId}
              decks={state.decks}
              folders={state.folders ?? []}
              onSelectDeck={handleStudyDeck}
              onStudyDirect={handleStudyDeckDirect}
              onStudyFolder={handleStudyFolder}
              onReviewFolder={handleReviewFolder}
              onNavTo={navTo}
              onRefreshFolders={refreshFolders}
              onCreateFolder={addFolder}
            />
          )}

          {!isCreateDeckRoute && isHomeRoute && !isHomeStudyRoute && state.currentView !== "dashboard" && state.currentView !== "gamification" && (
            <HomeDashboard
              decks={state.decks}
              onSelectDeck={handleStudyDeck}
              onNavTo={(v) => (v === "library" ? navigate("/library") : navTo("flashcards"))}
              onStartDueReview={handleStartDueReview}
              onStartSmartReview={handleStartSmartReview}
            />
          )}

          {!isCreateDeckRoute && isGlobalLibraryRoute && (
            <GlobalLibraryPage />
          )}

          {!isCreateDeckRoute && isLibraryRoute && !isLibraryStudyRoute && !isGlobalLibraryRoute && !isGlobalLibraryStudyRoute && state.currentView !== "dashboard" && state.currentView !== "gamification" && (
            <HomeView
              decks={state.decks}
              folders={state.folders ?? []}
              loading={state.loading}
              isTeacher={isTeacher}
              onSelectDeck={handleStudyDeck}
              onStudyDirect={handleStudyDeckDirect}
              onNewDeck={(type) => navigate(`/library/create?type=${type}`)}
              onCreateFolder={() => setShowFolderModal(true)}
              onEditDeck={(id) => navigate(`/library/edit/${id}`)}
              onDeleteDeck={handleDeleteDeck}
              onNavTo={navTo}
              onDeckShared={(deckId, token, shareAccess) =>
                updateDeck(deckId, { shareToken: token ?? undefined, shareAccess })
              }
              onRemoveTag={handleRemoveTag}
            />
          )}

          {isTestRoute && !isFolderRoute && !testRouteDeck && (
            <div style={{ textAlign: "center", padding: 60, color: "hsl(var(--muted-foreground))" }}>
              <div className="flex justify-center mb-4"><ClipboardList size={48} strokeWidth={1.5} className="opacity-70" /></div>
              <p>{t("shared.deckNotFound")}</p>
              <button
                onClick={() => navigate("/")}
                style={{
                  marginTop: 16,
                  padding: "10px 20px",
                  borderRadius: 50,
                  border: "none",
                  background: "hsl(var(--primary))",
                  color: "white",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {t("deckModal.backToHome")}
              </button>
            </div>
          )}

          {/* Study layout: mobile Deck Dashboard (native control center) | desktop (sidebar + main) */}
          {isFlashcardStudyRoute && studyDeck && (enabledModes.length > 0 || gamesEnabled) && !isGameModeRoute && (
            <>
              {(() => {
                const MODE_CONFIG: { id: StudyModeKey; labelKey: string; icon: typeof Layers; enabled: boolean }[] = [
                  { id: "cards", labelKey: "deckModal.studyModeCards", icon: Layers, enabled: !!studyModes.cards },
                  { id: "learn", labelKey: "deckModal.studyModeLearn", icon: GraduationCap, enabled: !!studyModes.learn },
                  { id: "test", labelKey: "deckModal.studyModeTest", icon: ClipboardList, enabled: !!studyModes.test },
                  { id: "games", labelKey: "studyMenu.games", icon: Gamepad, enabled: gamesEnabled },
                  { id: "philology", labelKey: "deckModal.studyModePhilology", icon: Network, enabled: !!studyModes.philology },
                ];
                const availableModes = MODE_CONFIG.filter((m) => m.enabled);
                const activeConfig = MODE_CONFIG.find((m) => m.id === activeStudyMode && m.enabled) ?? availableModes[0];
                const otherModes = availableModes.filter((m) => m.id !== activeStudyMode);

                /* Mobile or "Перейти в модуль" (showDashboardOnly): show Deck Dashboard first — modes 2x2 → Share/Edit → progress → Study */
                if (isMobile || showDashboardOnly) {
                  return (
                    <div className="flex flex-col gap-6 w-full -mt-1">
                      <div className="space-y-0.5">
                        <h1 className="text-xl md:text-2xl font-bold text-[hsl(var(--foreground))] truncate leading-tight">
                          {studyDeck.emoji && <span className="mr-1.5">{studyDeck.emoji}</span>}
                          {studyDeck.title}
                        </h1>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                          {t("sidebar.cardsCount", { count: studyDeck.cards?.length ?? 0 })}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {availableModes.map(({ id, labelKey, icon: Icon }) => {
                          const isActive = activeStudyMode === id;
                          return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              setActiveStudyMode(id);
                              if (id !== "cards") setIsCardsFullscreen(false);
                              if (id !== "learn") setStartedLearnMode(false);
                              if (id !== "test") setStartedTestMode(false);
                            }}
                            className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border shadow-sm transition-all min-h-[88px] opacity-100 ${
                              isActive
                                ? "bg-purple-100 dark:bg-purple-900/40 border-2 border-purple-500 dark:border-purple-500 text-purple-800 dark:text-purple-200 shadow-md"
                                : "bg-white dark:bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))] text-gray-800 dark:text-gray-200 hover:border-purple-300 dark:hover:border-purple-600"
                            }`}
                            aria-pressed={isActive}
                            aria-current={isActive ? "true" : undefined}
                          >
                            <Icon size={24} strokeWidth={2} className={`shrink-0 ${isActive ? "text-purple-600 dark:text-purple-300" : "text-gray-700 dark:text-gray-300"}`} />
                            <span className="text-sm font-medium text-center line-clamp-1 opacity-100">{t(labelKey)}</span>
                          </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowShareModal(true)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-gray-800 dark:text-gray-200 text-sm font-medium transition-colors shrink-0"
                          aria-label={t("share")}
                        >
                          <Share2 size={18} strokeWidth={2} />
                          {t("share")}
                        </button>
                        <button
                          type="button"
                          onClick={() => currentDeck && navigate(`/library/edit/${currentDeck.id}`)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-gray-800 dark:text-gray-200 text-sm font-medium transition-colors shrink-0"
                          aria-label={t("studyMenu.edit")}
                        >
                          <Pencil size={18} strokeWidth={2} />
                          {t("studyMenu.edit")}
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center justify-center w-10 h-10 rounded-xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-gray-700 dark:text-gray-300 transition-colors shrink-0"
                              aria-label={t("studyMenu.menu")}
                            >
                              <MoreHorizontal size={20} strokeWidth={2} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" sideOffset={8} className="w-56 bg-white dark:bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))] shadow-lg text-gray-900 dark:text-gray-100">
                            <DropdownMenuItem onClick={() => setShowSaveToFolderModal(true)} className="text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-500/20 cursor-pointer">
                              <Bookmark size={16} className="mr-3 shrink-0" />
                              {t("saveToFolder.saveButton")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleDuplicateDeck} disabled={isDuplicating} className="text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-500/20 cursor-pointer">
                              <Copy size={16} className="mr-3 shrink-0" />
                              {isDuplicating ? t("common.loading") : t("studyMenu.duplicate")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handlePrint} className="text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-500/20 cursor-pointer">
                              <Printer size={16} className="mr-3 shrink-0" />
                              {t("studyMenu.print")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowMergeModal(true)} className="text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-500/20 cursor-pointer">
                              <GitMerge size={16} className="mr-3 shrink-0" />
                              {t("studyMenu.merge")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowExportModal(true)} className="text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-500/20 cursor-pointer">
                              <Download size={16} className="mr-3 shrink-0" />
                              {t("studyMenu.export")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => currentDeck && setDeleteModalDeck(currentDeck)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/20 focus:bg-red-50 dark:focus:bg-red-500/20 cursor-pointer">
                              <Trash2 size={16} className="mr-3 shrink-0" />
                              {t("studyMenu.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {/* Unified: Progress + Action Button for all modes (or empty state) */}
                      {(!studyDeck.cards || studyDeck.cards.length === 0) && (
                        <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                          <p>{t("home.deckEmpty")}</p>
                          <button
                            type="button"
                            onClick={() => currentDeck && navigate(`/library/edit/${currentDeck.id}`)}
                            className="mt-4 px-6 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-white font-medium text-sm"
                          >
                            {t("studyMenu.edit")}
                          </button>
                        </div>
                      )}
                      {studyDeck.cards && studyDeck.cards.length > 0 && (() => {
                            const total = studyDeck.cards.length;
                            const cardsLivePct = ((state.cardIndex + 1) / total) * 100;
                            const displayPct = activeStudyMode === "cards"
                              ? Math.max(modeProgress.cards, Math.round(cardsLivePct))
                              : modeProgress[activeStudyMode];
                            const displayCurrent = activeStudyMode === "cards"
                              ? state.cardIndex + 1
                              : Math.round((displayPct / 100) * total);
                            return (
                        <React.Fragment key="deck-progress">
                          <div className="w-full">
                            <div className="flex justify-between text-sm font-medium text-[hsl(var(--muted-foreground))] mb-2">
                              <span>{t("flashcard.progress", { pct: displayPct })}</span>
                              <span>{t("studyMode.progress", { current: displayCurrent, total })}</span>
                            </div>
                            <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-300 min-w-[6px]"
                                style={{
                                  width: `${Math.min(100, displayPct > 0 ? Math.max(displayPct, 3) : displayPct)}%`,
                                  background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
                                }}
                              />
                            </div>
                          </div>
                          {activeStudyMode === "cards" && !isCardsFullscreen && (
                            <button
                              type="button"
                              onClick={() => setIsCardsFullscreen(true)}
                              className="w-full py-4 px-6 rounded-2xl text-lg font-bold text-white bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] hover:opacity-95 active:scale-[0.98] transition-all shadow-lg shadow-[hsl(var(--primary))]/25"
                              aria-label={t("deckDashboard.startCards")}
                            >
                              {t("deckDashboard.startCards")}
                            </button>
                          )}
                          {activeStudyMode === "learn" && !startedLearnMode && (
                            <button
                              type="button"
                              onClick={() => {
                                const url = new URL(window.location.href);
                                url.searchParams.set("type", "quiz");
                                window.history.replaceState({}, "", url.pathname + "?" + url.searchParams.toString());
                                setStartedLearnMode(true);
                              }}
                              className="w-full py-4 px-6 rounded-2xl text-lg font-bold text-white bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] hover:opacity-95 active:scale-[0.98] transition-all shadow-lg shadow-[hsl(var(--primary))]/25"
                              aria-label={t("deckDashboard.startLearn")}
                            >
                              {t("deckDashboard.startLearn")}
                            </button>
                          )}
                          {activeStudyMode === "test" && studyModes.test && !startedTestMode && (
                            <button
                              type="button"
                              onClick={() => {
                                const url = new URL(window.location.href);
                                url.searchParams.set("type", "test");
                                if (isMobile) url.searchParams.set("mode", "fullscreen");
                                const newSearch = url.searchParams.toString();
                                window.history.replaceState({}, "", url.pathname + "?" + newSearch);
                                setStartedTestMode(true);
                              }}
                              className="w-full py-4 px-6 rounded-2xl text-lg font-bold text-white bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] hover:opacity-95 active:scale-[0.98] transition-all shadow-lg shadow-[hsl(var(--primary))]/25"
                              aria-label={t("deckDashboard.startTest")}
                            >
                              {t("deckDashboard.startTest")}
                            </button>
                          )}
                          {activeStudyMode === "games" && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <Gamepad size={20} className="text-purple-600 dark:text-purple-400 shrink-0" />
                                <p className="text-sm font-semibold text-[hsl(var(--foreground))]">{t("deckDashboard.chooseGame")}</p>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {GAME_MODES.map(({ id, label, icon: Icon }) => {
                                  const gamePct = gameProgress[id] ?? 0;
                                  const isMastered = gamePct === 100;
                                  const isNewlyMastered = newlyMasteredGameIds.has(id);
                                  const displayWidth = Math.max(0, Math.min(100, gamePct));
                                  const hasProgress = displayWidth > 0;
                                  return (
                                  <button
                                    key={id}
                                    type="button"
                                    onClick={() => handleNavigateToGameMode(studyDeck.id, id, studyDeck)}
                                    onAnimationEnd={() => isNewlyMastered && clearNewlyMastered(id)}
                                    className={`group flex flex-col items-center justify-center gap-2.5 p-4 rounded-xl border-2 bg-white dark:bg-[hsl(var(--card-bg))] transition-all duration-200 opacity-100 text-gray-800 dark:text-gray-200 min-h-[100px] overflow-hidden relative cursor-pointer ${
                                      isMastered
                                        ? "border-amber-400 dark:border-amber-500 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-amber-500 dark:hover:border-amber-400"
                                        : "border-[hsl(var(--border))] hover:border-purple-400 dark:hover:border-purple-500 hover:bg-[hsl(var(--muted))]/30 dark:hover:bg-purple-500/10 hover:shadow-md hover:-translate-y-0.5"
                                    } ${isNewlyMastered ? "animate-game-mastery-shine" : ""}`}
                                    aria-label={isMastered ? `${label} (${t("deckDashboard.mastered")})` : label}
                                  >
                                    <div className="absolute inset-x-0 top-0 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-t-xl overflow-hidden">
                                      {isMastered ? (
                                        <div className="h-full w-full rounded-t-xl bg-amber-500 dark:bg-amber-400" />
                                      ) : (
                                        <div
                                          className="h-full rounded-tl rounded-r-full transition-all duration-300 min-w-[4px]"
                                          style={{
                                            width: hasProgress ? `${Math.max(displayWidth, 2)}%` : "0%",
                                            backgroundColor: "rgb(59, 130, 246)",
                                          }}
                                        />
                                      )}
                                    </div>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isMastered ? "bg-amber-100 dark:bg-amber-500/30" : "bg-[hsl(var(--muted))] dark:bg-purple-900/40 group-hover:bg-purple-100 dark:group-hover:bg-purple-500/20"}`}>
                                      <Icon size={22} className={isMastered ? "text-amber-600 dark:text-amber-400" : "text-purple-600 dark:text-purple-400"} strokeWidth={2} />
                                    </div>
                                    <span className="text-sm font-medium text-center line-clamp-2 leading-tight flex items-center justify-center gap-1.5">
                                      {isMastered && <Crown size={14} className="text-amber-500 dark:text-amber-400 shrink-0" aria-hidden />}
                                      {label}
                                    </span>
                                  </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {!startedLearnMode && !startedTestMode && activeStudyMode !== "games" && studyDeck.cards && studyDeck.cards.length > 0 && (
                            <ExpressReviewWidget deck={studyDeck} />
                          )}
                        </React.Fragment>
                            );
                          })()}
                      {activeStudyMode === "learn" && startedLearnMode && studyDeck.cards && studyDeck.cards.length > 0 && (
                        <div className="flex flex-col w-full h-[calc(100vh-20rem)] min-h-[320px] -mx-6 w-[calc(100%+3rem)]">
                          <LearnMode
                            cards={studyDeck.cards}
                            deckLang={studyDeck.lang ?? "en"}
                            deckId={studyDeck.id}
                            onBack={handleBackFromLearnMode}
                            onSessionEnd={(pct) => updateModeProgress("learn", pct)}
                            onCardResult={
                              user?.id && studyDeck.id
                                ? (cardId, correct) => {
                                    recordCardInteraction({
                                      userId: user.id,
                                      cardId,
                                      deckId: studyDeck.id,
                                      mode: "learn",
                                      answeredCorrectly: correct,
                                    }).catch((e) => console.warn("[Adaptive] recordCardInteraction failed:", e));
                                  }
                                : undefined
                            }
                          />
                        </div>
                      )}
                      {activeStudyMode === "philology" && studyModes.philology && studyDeck && studyDeck.cards && studyDeck.cards.length > 0 && (
                        <div className="flex flex-col w-full min-h-[400px]">
                          <PhilologyMode
                            deck={studyDeck}
                            cardIndex={state.cardIndex}
                            onPrevCard={handlePrevCard}
                            onNextCard={handleNextCard}
                            onSessionEnd={(pct) => updateModeProgress("philology", pct)}
                          />
                        </div>
                      )}
                      {activeStudyMode === "test" &&
                        startedTestMode &&
                        studyModes.test &&
                        studyDeck &&
                        !(isMobile && studyDeck?.cards?.length) && (
                        <div className="flex flex-col w-full min-h-[400px]">
                          <TestStudyView
                            deck={studyDeck}
                            onBack={() => {
                              setStartedTestMode(false);
                              const url = new URL(window.location.href);
                              url.searchParams.delete("type");
                              url.searchParams.delete("mode");
                              const newSearch = url.searchParams.toString();
                              window.history.replaceState({}, "", url.pathname + (newSearch ? `?${newSearch}` : ""));
                            }}
                            onTestComplete={(r) => updateModeProgress("test", r.total > 0 ? Math.round((r.correctCount / r.total) * 100) : 0)}
                            onEdit={() => navigate(`/library/edit/${studyDeck.id}`)}
                            onMenuClick={() => setSidebarOpen(true)}
                            onFullscreen={() => {
                              if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
                              else document.exitFullscreen?.();
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                /* Desktop: sidebar + main content. Hide sidebar in cards mode so flashcard is centered. */
                return (
            <div className="flex flex-col md:flex-row md:gap-6 w-full md:items-start -mt-1">
              <aside className={`hidden flex-col flex-nowrap gap-2 shrink-0 w-48 ${activeStudyMode === "cards" ? "md:hidden" : "md:flex"}`}>
                <div className="border border-[hsl(var(--border))] rounded-xl bg-white dark:bg-[hsl(var(--card-bg))] overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => otherModes.length > 0 && setModeDropdownOpen((o) => !o)}
                    className="w-full px-4 py-3 flex items-center justify-between gap-2 font-medium text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/50 transition-colors"
                    aria-expanded={otherModes.length > 0 ? modeDropdownOpen : false}
                    aria-haspopup={otherModes.length > 0 ? "listbox" : undefined}
                  >
                    <span className="flex items-center gap-2">
                      {activeConfig && <activeConfig.icon size={16} className="text-purple-600 dark:text-purple-400 shrink-0" />}
                      {activeConfig ? t(activeConfig.labelKey) : t("studyMenu.modeSelection")}
                    </span>
                    {otherModes.length > 0 && (
                      <ChevronDown size={16} className={`shrink-0 text-[hsl(var(--muted-foreground))] transition-transform duration-200 ${modeDropdownOpen ? "rotate-180" : ""}`} />
                    )}
                  </button>
                  {modeDropdownOpen && otherModes.length > 0 && (
                    <div className="border-t border-[hsl(var(--border))] py-1" role="listbox">
                      {otherModes.map(({ id, labelKey, icon: Icon }) => (
                        <button
                          key={id}
                          type="button"
                          role="option"
                          onClick={() => { setActiveStudyMode(id); setModeDropdownOpen(false); if (id !== "cards") setIsCardsFullscreen(false); }}
                          className="w-full px-4 py-2.5 flex items-center gap-2 text-sm text-[hsl(var(--foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                        >
                          <Icon size={16} className="shrink-0 opacity-70" />
                          {t(labelKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowSaveToFolderModal(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:bg-[hsl(var(--border))]/50 text-[hsl(var(--foreground))] text-xs font-medium transition-colors shrink-0" aria-label={t("saveToFolder.title")}>
                    <Bookmark size={14} strokeWidth={2} />
                    {t("saveToFolder.saveButton")}
                  </button>
                  <button type="button" onClick={() => setShowShareModal(true)} className="flex items-center justify-center w-10 h-10 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] transition-colors shrink-0" aria-label={t("share")}>
                    <Share2 size={18} strokeWidth={2} />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="flex items-center justify-center min-w-[36px] min-h-[36px] w-9 h-9 p-0 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] text-gray-600 dark:text-gray-400 transition-colors shrink-0 overflow-visible box-border" aria-label={t("studyMenu.menu")}>
                        <MoreHorizontal size={18} className="shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" sideOffset={8} className="w-56 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 dark:border-[hsl(var(--border))] shadow-lg text-gray-700 dark:text-[hsl(var(--foreground))]">
                      <DropdownMenuItem onClick={() => currentDeck && navigate(`/library/edit/${currentDeck.id}`)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Pencil size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDuplicateDeck} disabled={isDuplicating} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Copy size={16} className="mr-3 shrink-0" />
                        {isDuplicating ? t("common.loading") : t("studyMenu.duplicate")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handlePrint} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Printer size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.print")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowMergeModal(true)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <GitMerge size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.merge")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowExportModal(true)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Download size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.export")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-200 dark:bg-[hsl(var(--border))]" />
                      <DropdownMenuItem onClick={() => currentDeck && setDeleteModalDeck(currentDeck)} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/20 focus:bg-red-50 focus:text-red-700">
                        <Trash2 size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {studyDeck?.cards && studyDeck.cards.length > 0 && activeStudyMode !== "games" && (
                  <ExpressReviewWidget deck={studyDeck} />
                )}
              </aside>
              {/* Main content area */}
              <div className="flex-1 min-w-0 w-full">
          {activeStudyMode === "games" && (
            <section className="w-full max-w-6xl mx-auto px-1 sm:px-0">
              <div className="flex flex-col space-y-6 sm:space-y-8">
                <header className="space-y-1">
                  <h3 className="text-xl font-semibold text-[hsl(var(--foreground))] flex items-center gap-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                      <Gamepad size={22} className="shrink-0" />
                    </span>
                    {t("deckDashboard.chooseGame")}
                  </h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {t("deckDashboard.chooseGameHint")}
                  </p>
                </header>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5 md:gap-6">
                  {GAME_MODES.map(({ id, label, icon: Icon }) => {
                    const gamePct = gameProgress[id] ?? 0;
                    const isMastered = gamePct === 100;
                    const isNewlyMastered = newlyMasteredGameIds.has(id);
                    const displayWidth = Math.max(0, Math.min(100, gamePct));
                    const hasProgress = displayWidth > 0;
                    return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleNavigateToGameMode(studyDeck.id, id, studyDeck)}
                      onAnimationEnd={() => isNewlyMastered && clearNewlyMastered(id)}
                      className={`group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 min-h-[140px] overflow-hidden relative cursor-pointer transition-all duration-200 ${
                        isMastered
                          ? "border-amber-400 dark:border-amber-500 shadow-sm bg-white dark:bg-[hsl(var(--card-bg))] hover:shadow-md hover:-translate-y-1 hover:border-amber-500 dark:hover:border-amber-400"
                          : "border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:border-[hsl(var(--primary))]/50 dark:hover:border-purple-500/60 hover:bg-[hsl(var(--muted))]/30 dark:hover:bg-purple-500/10 hover:shadow-lg hover:-translate-y-1"
                      } ${isNewlyMastered ? "animate-game-mastery-shine" : ""}`}
                      aria-label={isMastered ? `${label} (${t("deckDashboard.mastered")})` : label}
                    >
                      <div className="absolute inset-x-0 top-0 h-1.5 bg-[hsl(var(--muted))] dark:bg-gray-700 rounded-t-2xl overflow-hidden">
                        {isMastered ? (
                          <div className="h-full w-full rounded-t-2xl bg-amber-500 dark:bg-amber-400" />
                        ) : (
                          <div
                            className="h-full rounded-tl-2xl rounded-r-full transition-all duration-300 min-w-[4px]"
                            style={{
                              width: hasProgress ? `${Math.max(displayWidth, 2)}%` : "0%",
                              backgroundColor: "hsl(var(--primary))",
                            }}
                          />
                        )}
                      </div>
                      <div className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center transition-colors ${
                        isMastered
                          ? "bg-amber-100 dark:bg-amber-500/30"
                          : "bg-[hsl(var(--muted))] dark:bg-purple-500/20 group-hover:bg-[hsl(var(--primary))]/10 dark:group-hover:bg-purple-500/30"
                      }`}>
                        <Icon size={24} className={`shrink-0 ${isMastered ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--foreground))] dark:text-purple-400"}`} strokeWidth={2} />
                      </div>
                      <span className="text-sm font-medium text-[hsl(var(--foreground))] text-center line-clamp-2 leading-tight flex items-center justify-center gap-1.5">
                        {isMastered && <Crown size={14} className="text-amber-500 dark:text-amber-400 shrink-0" aria-hidden />}
                        {label}
                      </span>
                    </button>
                    );
                  })}
                  {COMING_SOON_MODES.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => showToast(t("coming_soon"))}
                      className="group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] min-h-[140px] hover:border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--muted))]/20 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer relative"
                    >
                      <span className="absolute top-2 right-2 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">
                        {t("home.comingSoon")}
                      </span>
                      <div className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center bg-[hsl(var(--muted))] dark:bg-purple-500/20 group-hover:bg-[hsl(var(--primary))]/10 transition-colors">
                        <Icon size={24} className="text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={2} />
                      </div>
                      <span className="text-sm font-medium text-[hsl(var(--foreground))] text-center line-clamp-2">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}
                {studyDeck && (!studyDeck.cards || studyDeck.cards.length === 0) && (
                  <div style={{ textAlign: "center", padding: 60, color: "hsl(var(--muted-foreground))" }}>
                    <p>{t("dashboard.noCardsToReview")}</p>
                    <button
                      onClick={() => setStudyFilter("all")}
                      style={{ marginTop: 16, padding: "10px 20px", borderRadius: 50, border: "none", background: "hsl(var(--primary))", color: "white", fontFamily: "DM Sans, sans-serif", fontSize: 13, cursor: "pointer" }}
                    >
                      {t("home.studyAllCards")}
                    </button>
                  </div>
                )}
                {studyDeck && studyDeck.cards && studyDeck.cards.length > 0 && activeStudyMode === "learn" && (
                  <div className="flex flex-col w-full min-h-0 -mx-6 -mt-4 w-[calc(100%+3rem)]">
                    <LearnMode
                      cards={studyDeck.cards ?? []}
                      deckLang={studyDeck.lang ?? "en"}
                      deckId={studyDeck.id}
                      onSessionEnd={(pct) => updateModeProgress("learn", pct)}
                      onCardResult={
                        user?.id && studyDeck.id
                          ? (cardId, correct) => {
                              recordCardInteraction({
                                userId: user.id,
                                cardId,
                                deckId: studyDeck.id,
                                mode: "learn",
                                answeredCorrectly: correct,
                              }).catch((e) => console.warn("[Adaptive] recordCardInteraction failed:", e));
                            }
                          : undefined
                      }
                      onBack={
                        isFlashcardStudyRoute
                          ? () => {
                              if (window.history.length > 1) {
                                navigate(-1);
                              } else {
                                navigate(
                                  isLibraryStudyRoute ? "/library" : isFolderStudyRoute && urlFolderId ? `/folder/${urlFolderId}` : "/"
                                );
                              }
                            }
                          : isHomeRoute
                            ? () => navTo("home")
                            : isLibraryRoute
                              ? () => navTo("library")
                              : undefined
                      }
                    />
                  </div>
                )}
                {/* Philology: word family explorer (desktop and tablet). Mobile uses dedicated dashboard branch. */}
                {studyDeck && studyDeck.cards && studyDeck.cards.length > 0 && activeStudyMode === "philology" && !isMobile && (
                  <div className="w-full flex flex-1 flex-col min-h-0">
                    <PhilologyMode
                      deck={studyDeck}
                      cardIndex={state.cardIndex}
                      onPrevCard={handlePrevCard}
                      onNextCard={handleNextCard}
                      onSessionEnd={(pct) => updateModeProgress("philology", pct)}
                    />
                  </div>
                )}
                {/* Desktop: standard FlashcardView. Mobile uses Deck Dashboard or fullscreen overlay. */}
                {studyDeck && studyDeck.cards && studyDeck.cards.length > 0 && activeStudyMode === "cards" && !isMobile && (
                  <div className="w-full flex flex-1 flex-col min-h-0 overflow-visible -m-6">
                    <FlashcardView
                      key={`flashcard-desktop-${urlDeckId}-${state.cardIndex}-${(studyDeck.cards[state.cardIndex]?.id ?? state.cardIndex)}-${location.key}`}
                      deck={studyDeck}
                      cardIndex={state.cardIndex}
                      cardFlipped={state.cardFlipped}
                      isTeacher={isTeacher}
                      onMountReset={handleResetCardFlip}
                      onFlip={handleFlip}
                      onMark={handleMark}
                      onShuffle={handleShuffle}
                      onShowExplain={() => setShowExplain(true)}
                      onPrevCard={handlePrevCard}
                      onNextCard={handleNextCard}
                      onBack={undefined}
                      onUpdateCard={(cardIndex, updates) => {
                        if (!studyDeck) return;
                        const card = studyDeck.cards?.[cardIndex];
                        const fullDeck = state.decks.find((d) => d.id === studyDeck.id);
                        const fullIndex =
                          card?.id && fullDeck ? fullDeck.cards?.findIndex((c) => c.id === card.id) ?? -1 : -1;
                        const idx = fullIndex >= 0 ? fullIndex : cardIndex;
                        mergeCardInDeck(studyDeck.id, idx, updates);
                        refetchDeck(studyDeck.id);
                        if (typeof console !== "undefined" && console.info) {
                          console.info("[Study] study_card_state_synced_after_edit", {
                            cardId: card?.id,
                            hasImageUrl: updates.imageUrl !== undefined,
                            keys: Object.keys(updates),
                          });
                        }
                      }}
                      onCardSrsUpdated={(cardIndex, cardId, srsData) => {
                        if (!studyDeck) return;
                        const deck = state.decks.find((d) => d.id === studyDeck.id);
                        const fullIndex = deck?.cards?.findIndex((c) => c.id === cardId) ?? -1;
                        const idx = fullIndex >= 0 ? fullIndex : cardIndex;
                        mergeCardInDeck(studyDeck.id, idx, srsData);
                      }}
                    />
                  </div>
                )}
                {/* Test mode: rendered in fullscreen overlay above */}
              </div>
            </div>
          );
                })()}
            </>
          )}

          {/* Test view — standalone when NOT in wrapper (e.g. /test/:deckId route) */}
          {showTestView && !isFolderRoute && currentDeck && !(isFlashcardStudyRoute && studyDeck && (enabledModes.length > 0 || gamesEnabled) && !isGameModeRoute) && (
            <div className="w-full h-[calc(100vh-6rem)] min-h-0 flex flex-col flex-1">
            <>
              {isTestRoute && (
                <div className="flex justify-center mb-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center justify-center min-w-[40px] min-h-[40px] w-10 h-10 p-0 rounded-full border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] text-gray-600 dark:text-gray-400 transition-colors shrink-0 overflow-visible box-border"
                        aria-label={t("studyMenu.menu")}
                      >
                        <MoreHorizontal size={20} className="shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={8} className="w-56 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 dark:border-[hsl(var(--border))] shadow-lg text-gray-700 dark:text-[hsl(var(--foreground))]">
                      <DropdownMenuItem onClick={() => navigate(`/library/edit/${currentDeck.id}`)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Pencil size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDuplicateDeck} disabled={isDuplicating} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Copy size={16} className="mr-3 shrink-0" />
                        {isDuplicating ? t("common.loading") : t("studyMenu.duplicate")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handlePrint} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Printer size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.print")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowMergeModal(true)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <GitMerge size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.merge")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowExportModal(true)} className="hover:bg-purple-50 dark:hover:bg-purple-500/20 hover:text-purple-700 focus:bg-purple-50 focus:text-purple-700">
                        <Download size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.export")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-200 dark:bg-[hsl(var(--border))]" />
                      <DropdownMenuItem
                        onClick={() => setDeleteModalDeck(currentDeck)}
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/20 focus:bg-red-50 focus:text-red-700"
                      >
                        <Trash2 size={16} className="mr-3 shrink-0" />
                        {t("studyMenu.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              <div className="flex-1 min-h-0 w-full flex flex-col">
                <TestStudyView
                  deck={currentDeck}
                  onEdit={() => navigate(`/library/edit/${currentDeck.id}`)}
                  onMenuClick={() => setSidebarOpen(true)}
                  onFullscreen={() => {
                    if (!document.fullscreenElement) {
                      document.documentElement.requestFullscreen?.();
                    } else {
                      document.exitFullscreen?.();
                    }
                  }}
                />
              </div>
            </>
            </div>
          )}

          {/* Match game: premium full-screen layout; strict flexbox so header stays at top and grid doesn't clip */}
          {isGameModeRoute && (urlGameMode === "match" || isMatchGamePath) && (
            <div className="fixed inset-0 z-20 h-[100dvh] w-full flex flex-col bg-background pt-4 overflow-hidden" data-match-game-layout>
              <div className="flex-1 min-h-0 flex flex-col items-center w-full overflow-hidden">
                {!currentDeck ? (
                  <div className="flex flex-col items-center w-full max-w-4xl p-4">
                    <GameHeader onBack={() => navigate("/", { replace: true })} />
                    <div className="flex flex-col items-center justify-center gap-4 text-center text-muted-foreground py-12">
                      <p>{t("home.selectDeckHint")}</p>
                    </div>
                  </div>
                ) : !(currentDeck.cards?.length) ? (
                  <div className="flex flex-col items-center w-full max-w-4xl p-4">
                    <GameHeader onBack={handleBackFromGameMode} />
                    <div className="flex flex-col items-center justify-center gap-4 text-center text-muted-foreground py-12">
                      <p>{t("home.deckEmpty")}</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full w-full max-w-4xl flex flex-col overflow-hidden">
                    <MatchMode
                      key={`match-${currentDeck?.id ?? "unknown"}`}
                      cards={currentDeck?.cards ?? []}
                      onBack={handleBackFromGameMode}
                      onProgressComplete={(pct) => updateGameProgress("match", pct)}
                      onFavorite={currentDeck?.id ? () => toggleDeckFavorite(currentDeck.id) : undefined}
                      isFavorite={currentDeck?.id ? isDeckFavorite(currentDeck.id) : false}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {isGameModeRoute && urlGameMode && urlGameMode !== "match" && currentDeck && (
            <div className="flex flex-col items-center w-full">
              {urlGameMode !== "cat-mouse" && urlGameMode !== "tower" && urlGameMode !== "defender" && urlGameMode !== "memory-jumble" && urlGameMode !== "memory" && urlGameMode !== "rebus" && urlGameMode !== "iq" && urlGameMode !== "audio" && urlGameMode !== "blitz" && urlGameMode !== "swipe" && urlGameMode !== "mosaic" && (
                <button
                  type="button"
                  onClick={handleBackFromGameMode}
                  className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] text-gray-700 dark:text-gray-300 font-medium transition-colors"
                >
                  <ArrowLeft size={20} className="shrink-0" />
                  {t("action.back")}
                </button>
              )}
              {urlGameMode === "cat-mouse" && (
                <CatMouseMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("cat-mouse", pct)}
                  onCardResult={
                    user?.id && currentDeck.id
                      ? (cardId, correct) =>
                          recordCardInteraction({
                            userId: user.id,
                            cardId,
                            deckId: currentDeck.id,
                            mode: "games",
                            answeredCorrectly: correct,
                          }).catch((e) => console.warn("[Adaptive] recordCardInteraction failed:", e))
                      : undefined
                  }
                  onFavorite={() => toggleDeckFavorite(currentDeck.id)}
                  isFavorite={isDeckFavorite(currentDeck.id)}
                />
              )}
              {urlGameMode === "defender" && (
                <DefenderMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("defender", pct)}
                  onCardResult={
                    user?.id && currentDeck.id
                      ? (cardId, correct) =>
                          recordCardInteraction({
                            userId: user.id,
                            cardId,
                            deckId: currentDeck.id,
                            mode: "games",
                            answeredCorrectly: correct,
                          }).catch((e) => console.warn("[Adaptive] recordCardInteraction failed:", e))
                      : undefined
                  }
                  onFavorite={() => toggleDeckFavorite(currentDeck.id)}
                  isFavorite={isDeckFavorite(currentDeck.id)}
                />
              )}
              {urlGameMode === "tower" && (
                <TowerMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("tower", pct)}
                  onCardResult={
                    user?.id && currentDeck.id
                      ? (cardId, correct) =>
                          recordCardInteraction({
                            userId: user.id,
                            cardId,
                            deckId: currentDeck.id,
                            mode: "games",
                            answeredCorrectly: correct,
                          }).catch((e) => console.warn("[Adaptive] recordCardInteraction failed:", e))
                      : undefined
                  }
                />
              )}
              {urlGameMode === "memory-jumble" && (
                <MemoryJumbleMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("memory-jumble", pct)}
                />
              )}
              {urlGameMode === "memory" && (
                <MemoryMixMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("memory", pct)}
                />
              )}
              {urlGameMode === "rebus" && (
                <CipherRebusMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("rebus", pct)}
                />
              )}
              {urlGameMode === "iq" && (
                <IqMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("iq", pct)}
                />
              )}
              {urlGameMode === "audio" && (
                <SoundCloudsMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("audio", pct)}
                />
              )}
              {urlGameMode === "blitz" && (
                <BlitzRushMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("blitz", pct)}
                />
              )}
              {urlGameMode === "swipe" && (
                <SwipeArenaMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("swipe", pct)}
                />
              )}
              {urlGameMode === "mosaic" && (
                <SecretMosaicMode
                  cards={currentDeck.cards ?? []}
                  lang={currentDeck.lang}
                  onBack={handleBackFromGameMode}
                  onProgressComplete={(pct) => updateGameProgress("mosaic", pct)}
                />
              )}
            </div>
          )}

          {/* Other game modes without deck: show back and hint so no white screen */}
          {isGameModeRoute && urlGameMode && urlGameMode !== "match" && !currentDeck && (
            <div className="flex flex-col items-center justify-center w-full min-h-[50vh] gap-4 text-center text-muted-foreground">
              <p>{t("home.selectDeckHint")}</p>
              <button
                type="button"
                onClick={() => navigate(studyBackPath)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
              >
                <ArrowLeft size={18} />
                {t("action.back")}
              </button>
            </div>
          )}

          {/* Empty states when no deck or no cards — no sidebar (wrapper handles studyDeck case) */}
          {isFlashcardStudyRoute && !showTestView && !isGameModeRoute && !(studyDeck && (enabledModes.length > 0 || gamesEnabled)) && (
            <>
              {!currentDeck ? (
                <div style={{ textAlign: "center", padding: 60, color: "hsl(var(--muted-foreground))" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🃏</div>
                  <p>{t("home.selectDeckHint")}</p>
                  <button
                    onClick={() => navigate(studyBackPath)}
                    style={{ marginTop: 16, padding: "10px 20px", borderRadius: 50, border: "none", background: "hsl(var(--primary))", color: "white", fontFamily: "DM Sans, sans-serif", fontSize: 13, cursor: "pointer" }}
                  >
                    {t("home.goToDecks")}
                  </button>
                </div>
              ) : !(currentDeck?.cards?.length) ? (
                <div style={{ textAlign: "center", padding: 60, color: "hsl(var(--muted-foreground))" }}>
                  {t("home.deckEmpty")}
                </div>
              ) : null}
            </>
          )}

          {state.currentView === "dashboard" && !isTestRoute && !isFolderRoute && !isCreateDeckRoute && !isEditDeckRoute && (
            <DashboardView
              decks={state.decks}
              points={state.points}
              onSelectDeck={handleStudyDeck}
              onRepeatCard={async (deckId, cardId) => {
                const deck = await ensureDeckWithCards(deckId);
                if (deck?.cards?.length) {
                  setQuickStudy({ deckId, cardId });
                } else {
                  showToast(t("statistics.repeatLoadError"));
                }
              }}
            />
          )}
          {isGamificationRoute && !isTestRoute && !isFolderRoute && (
            <AchievementsDashboard
              asPage
              initialTab={gamificationTab}
            />
          )}

          {/* Fallback: ensure content area is never blank (e.g. during route transitions); exclude create/edit deck pages */}
          {!isFolderListRoute &&
            !isHomeRoute &&
            !isLibraryRoute &&
            !isCreateDeckRoute &&
            !isEditDeckRoute &&
            !isTestRoute &&
            !showTestView &&
            !(state.currentView === "flashcards" || isFlashcardStudyRoute) &&
            state.currentView !== "dashboard" &&
            !isGamificationRoute && (
              <div style={{ padding: 24, color: "hsl(var(--muted-foreground))" }}>
                <button
                  type="button"
                  onClick={() => navigate("/library")}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 50,
                    border: "none",
                    background: "hsl(var(--primary))",
                    color: "white",
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  ← {t("home.backToHome")}
                </button>
              </div>
            )}
        </main>
      </div>

      {/* Modals */}
      {showFolderModal && (
        <NewFolderModal
          isOpen={showFolderModal}
          onClose={() => setShowFolderModal(false)}
          onSubmit={handleCreateFolder}
        />
      )}
      {showSaveToFolderModal && currentDeck && user && (
        <SaveToFolderModal
          isOpen={showSaveToFolderModal}
          onClose={() => setShowSaveToFolderModal(false)}
          deckId={currentDeck.id}
          folders={state.folders ?? []}
          onCreateFolder={async (title) => {
            const folder = await addFolder(title);
            if (!folder) throw new Error("Not authenticated");
            return folder;
          }}
          onSave={async (folderId) => {
            await addDecksToFolder(folderId, [currentDeck!.id]);
            await refreshFolders();
          }}
        />
      )}
      {showSettings && (
        <SettingsModal
          state={state}
          onSave={(updates) => {
            updateState(updates);
            if (updates.theme != null) setSystemTheme(updates.theme);
          }}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showExplain && currentDeck && currentDeck.cards && state.cardIndex < currentDeck.cards.length && (
        <ExplainModal
          card={currentDeck.cards[state.cardIndex]}
          deck={currentDeck}
          uiLang={state.uiLang}
          onClose={() => setShowExplain(false)}
        />
      )}

      {quickStudy && (() => {
        const deck = state.decks.find((d) => d.id === quickStudy.deckId);
        if (!deck) return null;
        return (
          <QuickStudyModal
            deck={deck}
            initialCardId={quickStudy.cardId}
            isTeacher={!!isTeacher}
            geminiKey={state.geminiKey}
            uiLang={state.uiLang}
            onClose={() => setQuickStudy(null)}
          />
        );
      })()}

      {showExportModal && currentDeck && (
        <ExportModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          cards={currentDeck.cards ?? []}
          deckTitle={currentDeck.title}
        />
      )}

      {deleteModalDeck && (
        <DeleteDeckModal
          deck={deleteModalDeck}
          onConfirm={() => {
            const deckId = deleteModalDeck.id;
            handleDeleteDeck(deckId);
            setDeleteModalDeck(null);
            showToast(t("module_deleted_success", "Набор успешно удален"));
            navigate("/library");
          }}
          onCancel={() => setDeleteModalDeck(null)}
        />
      )}

      {showMergeModal && currentDeck && (
        <MergeModulesModal
          open={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          currentDeck={currentDeck}
          allDecks={state.decks}
          addDeck={addDeck}
          onMerged={handleMergeComplete}
        />
      )}

      {showShareModal && currentDeck && (
        <ShareModal
          deck={currentDeck}
          onClose={() => setShowShareModal(false)}
          onShared={(deckId, token, shareAccess) =>
            updateDeck(deckId, { shareToken: token ?? undefined, shareAccess })
          }
        />
      )}

      {/* Toast */}
      <Toast message={toast.message} visible={toast.visible} />
    </div>

    {/* Mobile: Bottom tab bar — outside overflow-hidden so FAB can float above the bar */}
    {!isStudyFullscreenOverlay && (
      <BottomTabBar
        currentView={effectiveView}
        onNavTo={(view) => {
          navTo(view);
          if (view === "home") navigate("/");
          if (view === "library") navigate("/library");
          if (view === "gamification") navigate("/gamification");
          if (view === "dashboard") navigate("/dashboard");
        }}
        onNewDeck={(type) => navigate(`/library/create?type=${type}`)}
        onCreateFolder={() => setShowFolderModal(true)}
      />
    )}
    </>
  );
}
